import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Stack,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { useCreateEntity, useEntityTypeWithSchemas } from '@/hooks';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import { useNotifications } from '@/stores/useNotificationStore';
import { JsonFormsRenderer } from './JsonFormsRenderer';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';
import { useCatalogLocationLabels } from '@/hooks/useCatalogLocationLabels';
import type { Entity } from '../types/Entity';

interface EntityCreateFormProps {
  entityType: string;
  onSuccess?: (entity: Entity) => void;
  onCancel?: () => void;
}

type AjvValidationError = { instancePath?: string; message?: string };

export const EntityCreateForm: React.FC<EntityCreateFormProps> = ({
  entityType,
  onSuccess,
  onCancel,
}) => {
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [validationErrors, setValidationErrors] = useState<AjvValidationError[]>([]);

  const {
    data: entityConfig,
    isLoading: configLoading,
    error: configError,
  } = useEntityTypeWithSchemas(entityType);
  const createMutation = useCreateEntity();
  const notifications = useNotifications();
  const isViewer = useWriteGuard();
  const { primaryLocationLabel, secondaryLocationLabel } = useCatalogLocationLabels();

  // Build label overrides for catalog entity type
  const labelOverrides = useMemo(() => {
    if (entityType !== 'catalog') return undefined;
    return {
      '#/properties/primary_location_id': primaryLocationLabel,
      '#/properties/secondary_location_id': secondaryLocationLabel,
    };
  }, [entityType, primaryLocationLabel, secondaryLocationLabel]);

  // Initialize form data with default values when schema loads
  useEffect(() => {
    if (entityConfig?.entity_schema?.properties) {
      const initialData: Record<string, unknown> = {};

      // Set default values from schema
      Object.entries(entityConfig.entity_schema.properties).forEach(([fieldName, fieldSchema]) => {
        const def = (fieldSchema as { default?: unknown }).default;
        if (def !== undefined) {
          initialData[fieldName] = def as unknown;
        }
      });

      setFormData(initialData);
    }
  }, [entityConfig]);

  const handleFormDataChange = (newData: Record<string, unknown>) => {
    setFormData(newData);
  };

  const handleValidationChange = (isValid: boolean, errors: unknown[]) => {
    setIsFormValid(isValid);
    setValidationErrors(errors as AjvValidationError[]);
  };

  const handleSubmit = async () => {
    if (!isFormValid) {
      notifications.error('Validation Error', 'Please fix validation errors before submitting');
      return;
    }

    try {
      const displayName =
        typeof (formData as Record<string, unknown>).display_name === 'string'
          ? ((formData as Record<string, unknown>).display_name as string)
          : typeof (formData as Record<string, unknown>).name === 'string'
            ? ((formData as Record<string, unknown>).name as string)
            : `New ${entityType}`;

      const identifier =
        typeof (formData as Record<string, unknown>).entity_identifier === 'string'
          ? ((formData as Record<string, unknown>).entity_identifier as string)
          : `${entityType.toUpperCase()}-${Date.now()}`;

      const entityData = {
        entity_type: entityType,
        display_name: displayName,
        entity_identifier: identifier,
        entity_data: formData,
        status: 'active',
      } as const;

      const newEntity = await createMutation.mutateAsync(entityData);

      notifications.success('Entity Created', `${entityType} created successfully`);

      onSuccess?.(newEntity);
    } catch (error) {
      notifications.error(
        'Creation Failed',
        `Failed to create ${entityType}: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  };

  const handleCancel = () => {
    setFormData({});
    onCancel?.();
  };

  if (configLoading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading form configuration...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (configError || !entityConfig?.entity_schema) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            Failed to load form configuration for {entityType}.
            {configError instanceof Error ? ` Error: ${configError.message}` : ''}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Create New {entityConfig.display_name || entityType}
        </Typography>

        <JsonFormsRenderer
          schema={entityConfig.entity_schema}
          uiSchema={entityConfig.ui_schema}
          data={formData}
          onChange={handleFormDataChange}
          onValidationChange={handleValidationChange}
          readonly={isViewer}
          labelOverrides={labelOverrides}
        />

        {validationErrors.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Please fix the following errors:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {validationErrors.map((error, index) => (
                <li key={index}>
                  {error.instancePath}: {error.message}
                </li>
              ))}
            </ul>
          </Alert>
        )}
      </CardContent>

      <CardActions>
        <Stack direction="row" spacing={2} sx={{ ml: 'auto' }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <ViewerGuardedButton
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={!isFormValid || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create'}
          </ViewerGuardedButton>
        </Stack>
      </CardActions>
    </Card>
  );
};
